import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useId } from "react";

import { HeroAvatar, type HeroView, useHeroLookup } from "@/features/matches";
import { cn } from "@/shared/lib";

import { heroTint } from "../../model/heroTint";
import type { PlayerSummary } from "../../model/PlayerSummary";
import type { ProfileCover } from "../../model/ProfileCover";

const MATCHES = msg({ message: "{count, plural, one {# match} other {# matches}}" });

type CoverOption = {
  key: string;
  cover: ProfileCover;
  hero: HeroView | null;
  name: string;
  details: string;
};

type CoverPickerProps = {
  id: string;
  // Выбранная обложка.
  cover: ProfileCover;
  // Герои игрока по убыванию матчей.
  heroes: PlayerSummary["heroes"];
  disabled: boolean;
  onPick: (cover: ProfileCover) => void;
};

const isSelected = (option: ProfileCover, cover: ProfileCover): boolean =>
  option.kind === cover.kind && (option.kind !== "hero" || option.hero_id === cover.hero_id);

// Выбор обложки под шапкой профиля: «Авто», герои игрока и «Без обложки». Плитка —
// цвет героя с его иконкой: полный арт тяжёлый, грузится только для выбранной обложки.
export const CoverPicker = ({ id, cover, heroes, disabled, onPick }: CoverPickerProps) => {
  const { i18n, t } = useLingui();
  const labelId = useId();
  const lookup = useHeroLookup();
  const top = heroes[0] === undefined ? null : lookup(heroes[0].hero_id);
  const topName = top?.name;
  const options: CoverOption[] = [
    {
      key: "auto",
      cover: { kind: "auto", hero_id: null },
      hero: top,
      name: t`Auto`,
      details: topName === undefined ? t`Most played hero` : t`${topName}, most played`,
    },
    ...heroes.map((stat) => {
      const hero = lookup(stat.hero_id);
      return {
        key: String(stat.hero_id),
        cover: { kind: "hero" as const, hero_id: stat.hero_id },
        hero,
        name: hero.name,
        details: i18n._({ ...MATCHES, values: { count: stat.matches } }),
      };
    }),
    {
      key: "none",
      cover: { kind: "none", hero_id: null },
      hero: null,
      name: t`No cover`,
      details: t`Plain background`,
    },
  ];

  return (
    <div
      id={id}
      role="group"
      aria-labelledby={labelId}
      className="flex flex-col gap-3 border-t border-border px-7 pt-4.5 pb-5.5"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1.5">
        <span id={labelId} className="font-semibold">
          <Trans>Profile cover</Trans>
        </span>
        <span className="text-[13px] text-subtle">
          <Trans>Hero art and color. “Auto” takes the hero you play most.</Trans>
        </span>
      </div>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2.5">
        {options.map((option) => {
          const selected = isSelected(option.cover, cover);
          return (
            <button
              key={option.key}
              type="button"
              aria-pressed={selected}
              disabled={disabled}
              onClick={() => {
                onPick(option.cover);
              }}
              className={cn(
                "flex flex-col gap-2 rounded-[10px] border-2 p-1.5 pb-2.5 text-left text-sm transition-colors disabled:opacity-60",
                selected
                  ? "border-accent bg-sidebar-active"
                  : "border-border bg-surface-raised hover:border-border-strong",
              )}
            >
              <span
                className="flex h-14 items-center justify-center rounded-md bg-surface-sunken"
                style={{ backgroundColor: heroTint(option.hero?.color ?? null) }}
              >
                {option.hero !== null && (
                  <HeroAvatar
                    name={option.hero.name}
                    iconUrl={option.hero.iconUrl}
                    size={40}
                    className="rounded-full"
                  />
                )}
              </span>
              <span className="flex flex-col px-0.5 leading-tight">
                <span className="font-semibold">{option.name}</span>
                <span className="text-xs text-subtle">{option.details}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
